import { Label } from 'components/ui/label';
import React, { ReactNode, useCallback } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import NumberFormat from 'react-number-format';
import InfoPopover from 'components/info-popover';

interface Iprops {
  config: PAYMENT_CONFIG.IProcessorConfiguration;
  control: Control<FieldValues, any>;
  /** Optional helper copy shown in a dismissible tooltip beside the label. */
  tooltip?: ReactNode;
}

const ConfigAmount = ({ config, control, tooltip }: Iprops) => {
  // Format the input value to display in dollars
  const formatValue = useCallback((value: string) => {
    return `$${(Number(value) / 100).toFixed(2)}`;
  }, []);

  // Remove formatting to store the value in cents
  const removeFormatting = useCallback((value: string) => {
    return value.replace(/\D/g, '') || '0';
  }, []);

  return (
    <div className="mb-3 mr-3">
      <div className="flex items-center gap-1 mb-1">
        <Label
          htmlFor={`config-${config.code}`}
          className="text-sm font-medium text-gray-blue-80"
        >
          {config.displayName}
        </Label>
        {tooltip && (
          <InfoPopover
            label={`More information about ${config.displayName}`}
            data-testid={`config-${config.code}-info`}
          >
            {tooltip}
          </InfoPopover>
        )}
      </div>
      <Controller
        name={config.code}
        control={control}
        rules={{ required: config.isRequired }}
        render={({ field }) => (
          <NumberFormat
            ref={field.ref}
            name={field.name}
            onBlur={field.onBlur}
            id={`config-${config.code}`}
            data-testid={`config-${config.code}`}
            prefix="$"
            fixedDecimalScale
            decimalScale={2}
            placeholder="$1.50"
            className="block w-full max-w-3xl rounded-md border border-input bg-gray-blue-1 px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            format={formatValue}
            value={parseInt(field.value)}
            onValueChange={(values) => {
              field.onChange(parseInt(removeFormatting(values.value)));
            }}
          />
        )}
      />
    </div>
  );
};

export default ConfigAmount;
